import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { User, onAuthStateChanged, signOut } from "firebase/auth";
import { auth, syncUserToDb } from "@/lib/firebase";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  phoneNumber: string | null;
  email: string | null;
  displayName: string | null;
  authProvider: string | null;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  phoneNumber: null,
  email: null,
  displayName: null,
  authProvider: null,
  logout: async () => {},
});

function deriveProvider(user: User | null): string | null {
  if (!user) return null;
  const providers = user.providerData;
  if (providers.length === 0) return null;
  if (providers.some((p) => p.providerId === "phone")) return "phone";
  if (providers.some((p) => p.providerId === "google.com")) return "google";
  if (providers.some((p) => p.providerId === "password")) return "email";
  return providers[0].providerId;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const syncedUids = useRef<Set<string>>(new Set());

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);

      // Auto-sync user to DB on first auth
      if (firebaseUser && !syncedUids.current.has(firebaseUser.uid)) {
        syncedUids.current.add(firebaseUser.uid);
        syncUserToDb(firebaseUser);
      }
    });
    return unsubscribe;
  }, []);

  const logout = async () => {
    await signOut(auth);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        phoneNumber: user?.phoneNumber || null,
        email: user?.email || null,
        displayName: user?.displayName || null,
        authProvider: deriveProvider(user),
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
